import { Component, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { resolveAppVersion } from '@app/constants';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@services/auth.service';
import { DeviceTypeService } from '@services/device-type.service';
import { NavigationService } from '@services/navigation.service';
import { SearchService } from '@services/search.service';
import { UserService } from '@services/user.service';

/**
 * The global top bar, rendered once by `AppComponent` at every breakpoint.
 *
 * It renders in three modes:
 * - hidden entirely on onboarding routes (the caller guards that)
 * - logo-only on `/auth` routes while logged out
 * - full bar (workspace switcher, help, notifications, profile) once logged in
 *
 * Navigation itself lives in `MainMenuComponent` — the sidebar on desktop, the
 * burger drawer opened from here on mobile/tablet.
 */
@Component({
  selector: 'app-global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
  standalone: false,
})
export class GlobalHeaderComponent {
  @ViewChild('mobileMenu') mobileMenu!: TemplateRef<unknown>;

  public readonly appVersion: string = resolveAppVersion('0.0.0');

  private mobileMenuRef: NgbOffcanvasRef | null = null;

  constructor(
    private readonly offcanvasService: NgbOffcanvas,
    private readonly navigationService: NavigationService,
    private readonly router: Router,
    private readonly userService: UserService,
    public readonly authService: AuthService,
    public readonly deviceType: DeviceTypeService,
    public readonly searchService: SearchService,
  ) {}

  public get isAuthRoute(): boolean {
    return this.router.url.includes('/auth');
  }

  public get showFullHeader(): boolean {
    return this.authService.isLoggedIn && !this.isAuthRoute;
  }

  /**
   * The extension embeds the SPA in an iframe and has no window chrome of its
   * own, so it needs an in-app close affordance. Detecting the iframe is what
   * makes the button appear — the old header gated it on a flag nothing set,
   * so it never rendered.
   */
  public get isEmbeddedInExtension(): boolean {
    return window !== window.parent;
  }

  /**
   * Toggles the burger drawer. `NgbOffcanvas.open` stacks a fresh panel on every
   * call, so the open ref is tracked and dismissed rather than re-opened.
   */
  public toggleMobileMenu(): void {
    if (this.mobileMenuRef) {
      this.mobileMenuRef.dismiss();
      return;
    }

    this.mobileMenuRef = this.offcanvasService.open(this.mobileMenu, {
      position: 'start',
      panelClass: 'med_mobile-menu-panel',
      backdropClass: 'med_mobile-menu-backdrop',
    });
    this.mobileMenuRef.result.finally(() => (this.mobileMenuRef = null));
  }

  public closeMobileMenu(): void {
    this.mobileMenuRef?.dismiss();
  }

  public openFeedback(): void {
    this.userService.openFeedbackPortal();
  }

  public openDocumentation(): void {
    window.open('https://documentation.medullar.com/', '_blank', 'noopener');
  }

  public goHome(): void {
    this.navigationService.navigateToSpaces();
  }

  public closeExtension(): void {
    window.parent.postMessage(['closeExtension'], '*');
  }
}
